import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { Configuracoes } from "../entities/configuracoes";
import { HttpService } from "./http-service";

export interface ContratoAssinatura {
  id: number;
  status: string;
  nome_documento: string;
  url_assinatura: string | null;
  url_documento_assinado: string | null;
  assinado_em: string | null;
  hash_documento: string | null;
  autorizado_por?: string | { id?: number; nome?: string; name?: string } | null;
  autorizado_em?: string | null;
}

export interface AutorizacaoAssinatura {
  pedido_id?: number;
  status?: string;
  autorizado_por?: ContratoAssinatura["autorizado_por"];
  autorizado_em?: string | null;
  permite_finalizar?: boolean;
  contrato_obrigatorio?: boolean;
  contrato?: ContratoAssinatura | null;
}

export interface ConsultaAssinatura {
  pedido_id: number;
  contrato_obrigatorio: boolean;
  contrato: ContratoAssinatura | null;
  permite_finalizar: boolean;
}

export interface SolicitacaoAssinatura {
  contrato_id: number;
  status: string;
  url_assinatura: string | null;
}

@Injectable({
  providedIn: "root",
})
export class AssinaturaService {
  constructor(private httpService: HttpService) {}

  consultar(
    pedidoId: number,
    consultarProvedor = false
  ): Observable<ConsultaAssinatura> {
    const options = this.httpService.getHttpOptions();
    return this.httpService.http.get<ConsultaAssinatura>(
      `${Configuracoes.API}assinatura/${pedidoId}`,
      {
        headers: options.headers,
        params: { consultar_provedor: String(consultarProvedor) },
      }
    );
  }

  visualizar(pedidoId: number): Observable<string> {
    const options = this.httpService.getHttpOptions();
    return this.httpService.http.get(
      `${Configuracoes.API}assinatura/${pedidoId}/contrato`,
      {
        headers: options.headers,
        responseType: "text",
      }
    ) as Observable<string>;
  }

  solicitar(pedidoId: number): Observable<SolicitacaoAssinatura> {
    const options = this.httpService.getHttpOptions();
    return this.httpService.http.post<SolicitacaoAssinatura>(
      `${Configuracoes.API}assinatura/${pedidoId}`,
      {},
      { headers: options.headers }
    );
  }

  autorizar(pedidoId: number, motivo: string): Observable<AutorizacaoAssinatura> {
    const options = this.httpService.getHttpOptions();
    return this.httpService.http.post<AutorizacaoAssinatura>(
      `${Configuracoes.API}assinatura/${pedidoId}/autorizar`,
      { motivo },
      { headers: options.headers }
    );
  }

  static rotuloStatus(status: string | null | undefined): string {
    switch (status) {
      case "AGUARDANDO_ASSINATURA":
        return "Aguardando assinatura";
      case "ASSINADO":
        return "Assinado";
      case "AUTORIZADO":
        return "Dispensado";
      case "RECUSADO":
        return "Assinatura recusada";
      case "EXPIRADO":
        return "Assinatura expirada";
      case "CANCELADO":
        return "Contrato cancelado";
      case "ERRO":
        return "Não foi possível enviar o contrato";
      case "PENDENTE":
      default:
        return "Contrato ainda não enviado";
    }
  }

  static mensagem(error: any): string {
    const detail = error?.error?.detail ?? error?.error;
    if (!detail) {
      return "Não foi possível concluir a operação.";
    }
    if (typeof detail === "string") {
      return detail;
    }
    if (detail.codigo === "CONTRATO_DADOS_INCOMPLETOS") {
      return detail.mensagem || "Faltam dados do termo.";
    }
    const conhecidas: Record<string, string> = {
      PEDIDO_SEM_CLIENTE: "Pedido sem cliente vinculado",
      SIGNATARIO_SEM_EMAIL: "Cliente sem e-mail válido",
      SIGNATARIO_EMAIL_INVALIDO: "E-mail do cliente inválido",
      ACESSO_NEGADO: "Usuário sem acesso ao pedido",
      PEDIDO_NAO_ENCONTRADO: "Pedido inexistente",
      ASSINATURA_NAO_OBRIGATORIA:
        "A assinatura não é obrigatória para este pedido.",
      CONTRATO_NAO_ASSINADO:
        "É necessário assinar o contrato antes de finalizar o pedido.",
      CONTRATO_VERSAO_DIVERGENTE:
        "O contrato assinado não corresponde à versão atual do pedido. Solicite uma nova assinatura.",
      AUTENTIQUE_INDISPONIVEL:
        "Falha na Autentique. Não foi possível finalizar.",
    };
    return (
      conhecidas[detail.codigo] ||
      detail.mensagem ||
      "Não foi possível concluir a operação."
    );
  }
}
